import type { Doc } from "building-types/doc";
import type { BuiltInLabel } from "building-types/entities/labelTypes";
import { BuiltinPlanViewType } from "building-types/views/BuiltinPlanViewType";
import type { PlanViewLevelMember } from "building-types/views/View3D";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import { snakeCaseToTitleCase } from "common/utils/strings";
import type { PartialRecord } from "common/utils/types";
import docAddEntity from "./entity/docAddEntity";
import { elevationReferenceFrames } from "./options/elevationReferenceFrames";

export function docInitDefaultViews(doc: Doc): Doc {
  doc = docInitView3Ds(doc);
  doc = docInitElevationViews(doc);
  return docInitPlanViews(doc);
}

export function docAddDefaultPlanViewsForLevel(doc: Doc, levelId: string): Doc {
  for (const referenceType of [BuiltinPlanViewType.FLOOR, BuiltinPlanViewType.ELECTRICAL]) {
    doc = docAddEntity(doc, "PLAN_VIEW", {
      name: `${doc.levels[levelId].name} - ${snakeCaseToTitleCase(referenceType)}`,
      levelIds: [{ entityId: levelId }],
      referenceType,
    });
  }
  return doc;
}

export function docAddDefaultViewsForLevel(doc: Doc, levelId: string): Doc {
  doc = docAddEntity(doc, "VIEW_3D", { levelId, name: doc.levels[levelId].name });
  return docAddDefaultPlanViewsForLevel(doc, levelId);
}

export function docAddDefaultPlanViewsForCompositeLevel(doc: Doc, levelId: string): Doc {
  doc = docAddEntity(doc, "PLAN_VIEW", {
    name: `${doc.compositeLevels[levelId].name} - ${snakeCaseToTitleCase(BuiltinPlanViewType.FLOOR)}`,
    levelIds: [{ entityId: levelId }],
    referenceType: BuiltinPlanViewType.FLOOR,
    planViewType: "COMPOSITE",
  });
  return doc;
}

/**
 * Add builtin elevation views to doc
 */
export function docInitElevationViews(doc: Doc): Doc {
  for (const referenceFrame of Object.values(elevationReferenceFrames)) {
    const labels: PartialRecord<BuiltInLabel, true> = { addToExteriorElevation: true };
    doc = docAddEntity(doc, "ELEVATION_VIEW", {
      referenceFrame,
      viewFilter: { labels },
    });
  }

  return doc;
}

/**
 * This should be called when creating a new document
 */
export function docInitView3Ds(doc: Doc): Doc {
  // for each level init a new plan
  for (const levelId of sortTableBySortIndex(doc.levels)
    .map((x) => x.id)
    .reverse()) {
    doc = docAddEntity(doc, "VIEW_3D", { name: doc.levels[levelId].name, levelId });
  }

  // the default view has shows the while building without a cut level
  doc = docAddEntity(doc, "VIEW_3D", { name: "3D View" });

  return doc;
}

/**
 * Adds plans of each plan view type to the plan view table
 */
export function docInitPlanViews(doc: Doc): Doc {
  // add plan views for each level
  for (const levelId of sortTableBySortIndex(doc.levels)
    .map((view) => view.id)
    .reverse()) {
    doc = docAddDefaultPlanViewsForLevel(doc, levelId);
  }

  // add single foundation plan on lowest level
  doc = docAddEntity(doc, "PLAN_VIEW", {
    name: "Foundation",
    levelIds: [{ entityId: sortTableBySortIndex(doc.levels).map((x) => x.id)[0] }],
    referenceType: BuiltinPlanViewType.FOUNDATION,
  });

  // add single roof plan including all levels
  doc = docAddEntity(doc, "PLAN_VIEW", {
    name: `Roof`,
    levelIds: sortTableBySortIndex(doc.levels).map((x) => ({ entityId: x.id }) as PlanViewLevelMember),
    referenceType: BuiltinPlanViewType.ROOF,
  });

  return doc;
}
